/**
 * retiredSurfaces — where everything that lived on the Ghost page now lives.
 *
 * ONE-GHOST-2. The Ghost page carried three things a merchant could not get
 * anywhere else: the overnight briefing, the "bot team", and the approval
 * queue. This module is the single statement of where each one went, and of
 * where every route that used to point at the page now goes.
 *
 * It is pure on purpose. The redirect table is the thing a bookmark depends
 * on, so it has to be assertable without mounting a router.
 */

/** The queue's home. Real money and real drafts live behind this. */
export const APPROVALS_PATH = '/dashboard/approvals'

/** The retired page. Nothing may render here any more. */
export const RETIRED_GHOST_PATH = '/dashboard/ghost'

/** Where the panel launcher lives, and so where a retired link lands. */
export const PANEL_HOME_PATH = '/dashboard'

export type RetiredSurface = 'briefing' | 'bot_team' | 'approval_queue'

export interface SurfaceHome {
  /** Where a merchant now finds it. Null means it is not a place at all. */
  path: string | null
  /** True when the panel is the home rather than a route. */
  inPanel: boolean
}

/**
 * WHERE EACH ONE WENT, and why.
 *
 * The queue is the only one that is still a PLACE. It carries real drafts
 * that go out to real customers, and triaging hundreds of them is a work
 * surface, not a sentence in a side panel. It keeps a route; the panel is how
 * a merchant is TOLD about it and how a single draft made in conversation is
 * approved without leaving the thread.
 *
 * The briefing and the bot team are not places. A briefing is something Ghost
 * SAYS when you open him. And the "bot team" was five prompt prefixes over
 * one identical set of tools — the registry's own comment says every Bot
 * keeps ALL tools and that the favoured-tools hint "is prioritization, never
 * permission". Giving five faces of one Ghost their own page is precisely the
 * two-assistants problem this wave exists to end, so it does not get one.
 */
export const SURFACE_HOMES: Record<RetiredSurface, SurfaceHome> = {
  briefing: { path: null, inPanel: true },
  bot_team: { path: null, inPanel: true },
  approval_queue: { path: APPROVALS_PATH, inPanel: false },
}

/**
 * Where a request for the retired page (or any of its deep links) should
 * land. Never null: a bookmark always goes somewhere sensible.
 *
 * A link that named a PROPOSAL is the one deep link carrying real work, so it
 * is carried through to the queue's new home rather than dropped at the
 * dashboard. Everything else pointed at a conversation or a Bot, and both of
 * those now live in the panel, which opens over the dashboard.
 */
export function redirectForRetiredPath(pathAndSearch: string): string {
  const q = pathAndSearch.indexOf('?')
  const search = q === -1 ? '' : pathAndSearch.slice(q + 1)
  const params = new URLSearchParams(search)
  const proposal = params.get('proposal')
  if (proposal) return `${APPROVALS_PATH}?proposal=${encodeURIComponent(proposal)}`
  return PANEL_HOME_PATH
}

/**
 * ONE-GHOST-2 — THE BODY THE PANEL SENDS TO THE CHAT DOOR.
 *
 * Extracted so it can be EXECUTED by a test rather than read as source. Two
 * things must hold forever and they pull in opposite directions:
 *
 *  - NO ACCOUNT, EVER. Law 1 says the door resolves the business from the
 *    caller's credential and never accepts an identifier. If this function
 *    ever grows a sub_account_id the panel stops being lawful.
 *  - THE THREAD ID, ALWAYS, once there is one. Without it every question
 *    opened a new chat: the panel showed one conversation while the brain saw
 *    unrelated one-shots, so Ghost could write a draft and then have no memory
 *    of writing it when told to send it. Drafting cannot complete that way.
 */
export function panelChatBody(message: string, chatId: string | null): Record<string, string> {
  return chatId ? { message, chat_id: chatId } : { message }
}
